// TerritoryPanel.tsx — Coverage-Matrix Wave 2 (UI-A, AI-kh9r).
// Right-side panel scoped to a (productGroup, territory). Three layers:
//   L1 Executives — edited INLINE here (assign picker + remove) with the
//      escalation-on-assign prompt (spec §4).
//   L2 Managers / escalation — read-only summary + "Edit escalation" button that
//      OVERLAYS the existing M5 Escalation editor (via onEditEscalation bubble).
//   L3 Dealers — read-only + "Assign / remove dealers" button that OVERLAYS the
//      existing dealer modal (via onEditDealers bubble). Subject to §6.
// Writes go through the same authoring API (leadRouterV2Api) the wizard uses.

import { useEffect, useState } from 'react';
import { X, UserPlus, Trash2, TrendingUp, Store, ShieldCheck } from 'lucide-react';
import {
  leadRouterV2Api,
  type RouterStaff, type RouterStaffGraph,
  type TerritoryDetailV2, type MatchTypeV2,
} from '@/api/leadRouter';
import {
  buildAssignExecPayload, isManager, escalationTargetForRep,
} from './coverageLogic';

export interface TerritoryPanelProps {
  productGroupKey: string;
  productLabel: string;
  matchType: MatchTypeV2;
  matchValue: string;
  /** Human trail e.g. "SS Tanks · Haryana · Panipat". */
  breadcrumbLabel: string;
  staff: RouterStaff[];
  staffGraph: RouterStaffGraph;
  fallbackUserId: string | null;
  onClose: () => void;
  /** Overlay the M5 escalation editor for this manager. */
  onEditEscalation: (managerId: string) => void;
  /** Overlay the existing dealer modal for this manager (null → no manager to scope). */
  onEditDealers: (managerId: string | null) => void;
  /** Bump when an assignment write should refresh the matrix behind the panel. */
  onChanged?: () => void;
}

export function TerritoryPanel(props: TerritoryPanelProps) {
  const {
    productGroupKey, productLabel, matchType, matchValue, breadcrumbLabel,
    staff, staffGraph, fallbackUserId, onClose, onEditEscalation, onEditDealers, onChanged,
  } = props;

  const [detail, setDetail] = useState<TerritoryDetailV2 | null>(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [picking, setPicking] = useState(false);
  const [pickId, setPickId] = useState('');
  // Escalation prompt: holds the just-assigned rep + their team manager.
  const [escPrompt, setEscPrompt] = useState<{ userId: string; managerId: string } | null>(null);

  const nameOf = (id: string) => staff.find((s) => s.id === id)?.name ?? id;

  const reload = async () => {
    setLoading(true); setErr(null);
    try {
      setDetail(await leadRouterV2Api.getTerritoryDetail({ productGroupKey, matchType, matchValue }));
    } catch (e: any) {
      setErr(e?.response?.data?.error ?? e?.message ?? 'Failed to load territory');
    } finally { setLoading(false); }
  };
  useEffect(() => { void reload(); }, [productGroupKey, matchType, matchValue]);

  // Which manager the dealer overlay should scope to: a manager already on this
  // territory, else the team-manager of the first exec here.
  const dealerScopeManagerId =
    detail?.managers[0]?.id ??
    (detail?.execs[0] ? escalationTargetForRep(detail.execs[0].id, staffGraph) : null) ??
    null;

  async function assignExec(userId: string) {
    setBusy(true); setErr(null);
    try {
      await leadRouterV2Api.bulkSaveAssignments({
        assignments: [buildAssignExecPayload({ userId, productGroupKey, matchType, matchValue })],
        fallbackUserId,
      });
      setPicking(false); setPickId('');
      await reload();
      onChanged?.();
      // Escalation-on-assign prompt: only for reps (non-managers) with a team manager.
      if (!isManager(userId, staffGraph)) {
        const managerId = escalationTargetForRep(userId, staffGraph);
        if (managerId) setEscPrompt({ userId, managerId });
      }
    } catch (e: any) {
      const body = e?.response?.data;
      if (e?.response?.status === 422) {
        setErr(body?.error ?? 'Assignment blocked — this leaves an area with no owner and no fallback.');
      } else {
        setErr(body?.error ?? e?.message ?? 'Assign failed');
      }
    } finally { setBusy(false); }
  }

  async function removeExec(userId: string) {
    setBusy(true); setErr(null);
    try {
      // territory-detail returns user ids, not assignment ids — resolve the row.
      const rows = await leadRouterV2Api.listAssignments({ userId, layer: 'EXEC', active: true });
      const mv = matchValue.trim().toLowerCase();
      const hit = rows.find((r) =>
        r.matchType === matchType &&
        r.matchValue.trim().toLowerCase() === mv &&
        (r.productGroupKey === null || r.productGroupKey === productGroupKey),
      );
      if (hit) await leadRouterV2Api.deleteAssignment(hit.id);
      await reload();
      onChanged?.();
    } catch (e: any) {
      setErr(e?.response?.data?.error ?? e?.message ?? 'Remove failed');
    } finally { setBusy(false); }
  }

  async function resolveEscalation(enabled: boolean) {
    if (!escPrompt) return;
    setBusy(true);
    try {
      await leadRouterV2Api.putEscalation({ managerUserId: escPrompt.managerId, productGroupKey, enabled });
      onChanged?.();
    } catch (e: any) {
      setErr(e?.response?.data?.error ?? e?.message ?? 'Escalation update failed');
    } finally {
      setEscPrompt(null);
      setBusy(false);
    }
  }

  const assignedExecIds = new Set(detail?.execs.map((e) => e.id) ?? []);
  const pickableStaff = staff.filter((s) => !assignedExecIds.has(s.id));

  return (
    <div data-testid="territory-panel" style={panelStyle} role="dialog" aria-label="Territory detail">
      {/* Header */}
      <div style={{ padding: '14px 16px', borderBottom: '1px solid var(--line)', display: 'flex', alignItems: 'flex-start', gap: 10 }}>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 11, fontWeight: 800, textTransform: 'uppercase', letterSpacing: '.05em', color: 'var(--ink-3)' }}>Territory</div>
          <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--ink-0)' }}>{breadcrumbLabel}</div>
          {detail && (
            <div style={{ fontSize: 11.5, color: 'var(--ink-3)', marginTop: 2 }}>
              {productLabel} · {detail.hasExec ? 'has an exec owner' : 'NO exec owner (dealers blocked)'}
            </div>
          )}
        </div>
        <button type="button" onClick={onClose} style={iconBtn} aria-label="Close"><X size={16} /></button>
      </div>

      <div style={{ flex: 1, minHeight: 0, overflow: 'auto', padding: 16 }}>
        {loading && <div style={{ color: 'var(--ink-3)', fontSize: 13 }}>Loading territory…</div>}
        {err && <div style={{ color: 'var(--danger, #b5433a)', fontSize: 12.5, marginBottom: 10 }}>{err}</div>}

        {detail && (
          <>
            {/* ── Layer 1 — Executives (inline) ── */}
            <SectionLabel icon={<UserPlus size={13} />} text="Executives — Layer 1 (inline)" />
            <div style={{ marginBottom: 8 }}>
              {detail.execs.length === 0 && (
                <div style={{ fontSize: 12.5, color: 'var(--ink-3)', marginBottom: 6 }}>No exec assigned here — this territory falls to the triage fallback.</div>
              )}
              {detail.execs.map((e) => (
                <div key={e.id} data-testid="panel-exec-row" style={rowStyle}>
                  <span style={{ fontSize: 13.5, fontWeight: 600, color: 'var(--ink-0)' }}>{e.name}</span>
                  <div style={{ flex: 1 }} />
                  <button type="button" disabled={busy} onClick={() => void removeExec(e.id)}
                    style={{ ...miniBtn, color: 'var(--danger,#b5433a)' }} aria-label={`Remove ${e.name}`}>
                    <Trash2 size={12} /> Remove
                  </button>
                </div>
              ))}
            </div>
            {picking ? (
              <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 6 }}>
                <select value={pickId} onChange={(ev) => setPickId(ev.target.value)} style={selectStyle} aria-label="Pick exec">
                  <option value="">— pick a person —</option>
                  {pickableStaff.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
                </select>
                <button type="button" disabled={!pickId || busy} onClick={() => pickId && void assignExec(pickId)}
                  style={{ ...miniBtn, background: 'var(--forest,#2f7d3b)', color: '#fff', border: 'none', opacity: (!pickId || busy) ? 0.5 : 1 }}>Assign</button>
                <button type="button" onClick={() => { setPicking(false); setPickId(''); }} style={miniBtn}>Cancel</button>
              </div>
            ) : (
              <button type="button" onClick={() => setPicking(true)} style={{ ...miniBtn, marginBottom: 6 }}>
                <UserPlus size={12} /> Assign exec
              </button>
            )}

            {/* ── Layer 2 — Managers / escalation (overlay) ── */}
            <div style={{ height: 16 }} />
            <SectionLabel icon={<TrendingUp size={13} />} text="Managers / escalation — Layer 2 (overlay)" />
            {detail.managers.length === 0 && (
              <div style={{ fontSize: 12.5, color: 'var(--ink-3)', marginBottom: 6 }}>No manager escalation covers this territory.</div>
            )}
            {detail.managers.map((m) => (
              <div key={m.id} style={rowStyle}>
                <span style={{ fontSize: 13.5, fontWeight: 600, color: 'var(--ink-0)' }}>{m.name}</span>
                <span style={{ ...pill, background: m.escalationOn ? 'var(--forest-100,#d6ecd9)' : 'var(--ivory-1)', color: m.escalationOn ? 'var(--forest,#2f7d3b)' : 'var(--ink-3)' }}>
                  {m.escalationOn ? 'escalation ON' : 'escalation OFF'}
                </span>
                <div style={{ flex: 1 }} />
                <button type="button" onClick={() => onEditEscalation(m.id)} style={miniBtn}>
                  <TrendingUp size={12} /> Edit escalation
                </button>
              </div>
            ))}

            {/* ── Layer 3 — Dealers (overlay) ── */}
            <div style={{ height: 16 }} />
            <SectionLabel icon={<Store size={13} />} text="Dealers — Layer 3 (overlay)" />
            {detail.dealers.length === 0 && (
              <div style={{ fontSize: 12.5, color: 'var(--ink-3)', marginBottom: 6 }}>No dealer assigned to serve this territory.</div>
            )}
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 8 }}>
              {detail.dealers.map((d) => (
                <span key={d.id} style={{ ...pill, background: 'var(--warn-bg,#f7eccf)', color: 'var(--warn,#a5741a)' }}>{d.name}</span>
              ))}
            </div>
            {!detail.hasExec && (
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: 7, fontSize: 11.5, color: 'var(--danger,#b5433a)', marginBottom: 6 }}>
                <ShieldCheck size={13} style={{ flexShrink: 0, marginTop: 1 }} />
                <span>Assign an exec to this territory first — a dealer can only work under a rep owner.</span>
              </div>
            )}
            <button type="button" disabled={!detail.hasExec || !dealerScopeManagerId}
              onClick={() => onEditDealers(dealerScopeManagerId)}
              title={!detail.hasExec ? 'Blocked — assign an exec owner first' : (!dealerScopeManagerId ? 'No manager to scope the dealer modal to' : undefined)}
              style={{ ...miniBtn, opacity: (!detail.hasExec || !dealerScopeManagerId) ? 0.5 : 1, cursor: (!detail.hasExec || !dealerScopeManagerId) ? 'not-allowed' : 'pointer' }}>
              <Store size={12} /> Assign / remove dealers
            </button>
          </>
        )}
      </div>

      {/* Escalation-on-assign prompt */}
      {escPrompt && (
        <div style={{ position: 'absolute', inset: 0, background: 'rgba(20,15,10,0.35)', display: 'grid', placeItems: 'center', zIndex: 5, padding: 16 }}>
          <div data-testid="escalation-prompt" style={{ width: 'min(360px,92%)', background: 'var(--ivory-0)', border: '1px solid var(--line)', borderRadius: 12, padding: 16, boxShadow: '0 10px 40px rgba(0,0,0,0.25)' }}>
            <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--ink-0)', marginBottom: 6 }}>Escalate this rep’s hot leads?</div>
            <div style={{ fontSize: 12.5, color: 'var(--ink-2)', lineHeight: 1.5, marginBottom: 14 }}>
              Do <b>{nameOf(escPrompt.userId)}</b>’s HOT / untouched leads escalate to their team manager <b>{nameOf(escPrompt.managerId)}</b> for <b>{productLabel}</b>?
            </div>
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button type="button" disabled={busy} onClick={() => void resolveEscalation(false)} style={miniBtn}>No</button>
              <button type="button" disabled={busy} onClick={() => void resolveEscalation(true)}
                style={{ ...miniBtn, background: 'var(--forest,#2f7d3b)', color: '#fff', border: 'none' }}>Yes, escalate</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function SectionLabel({ icon, text }: { icon: React.ReactNode; text: string }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 11, fontWeight: 800, textTransform: 'uppercase', letterSpacing: '.05em', color: 'var(--ink-3)', marginBottom: 8 }}>
      {icon} {text}
    </div>
  );
}

const panelStyle: React.CSSProperties = {
  position: 'absolute', top: 0, right: 0, bottom: 0, width: 'min(420px, 92%)',
  background: 'var(--ivory-0)', borderLeft: '1px solid var(--line)',
  boxShadow: '-8px 0 30px rgba(0,0,0,0.12)', display: 'flex', flexDirection: 'column', zIndex: 4,
};
const iconBtn: React.CSSProperties = { display: 'grid', placeItems: 'center', width: 30, height: 30, border: '1px solid var(--line)', borderRadius: 8, background: 'var(--ivory-0)', cursor: 'pointer', color: 'var(--ink-2)' };
const rowStyle: React.CSSProperties = { display: 'flex', alignItems: 'center', gap: 8, padding: '7px 0', borderBottom: '1px solid var(--line)' };
const miniBtn: React.CSSProperties = { display: 'inline-flex', alignItems: 'center', gap: 5, padding: '5px 10px', fontSize: 12, border: '1px solid var(--line)', borderRadius: 7, background: 'var(--ivory-0)', cursor: 'pointer', color: 'var(--ink-1)' };
const pill: React.CSSProperties = { fontSize: 11, padding: '2px 8px', borderRadius: 999, border: '1px solid var(--line)' };
const selectStyle: React.CSSProperties = { flex: 1, height: 32, padding: '0 8px', fontSize: 12.5, border: '1px solid var(--line)', borderRadius: 8, background: 'var(--ivory-0)', color: 'var(--ink-1)' };

export default TerritoryPanel;
